import { useEffect, useState } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { FOCUS_TRAP_OPTIONS } from '@/constants/focus-trap';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DeleteAdjuntoConfirmModalProps {
  open: boolean;
  fileName: string;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function DeleteAdjuntoConfirmModal({
  open,
  fileName,
  pending,
  onClose,
  onConfirm,
}: DeleteAdjuntoConfirmModalProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => { setVisible(true); });
    } else {
      setVisible(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <FocusTrap focusTrapOptions={FOCUS_TRAP_OPTIONS}>
      <div
        className={cn(
          'fixed inset-0 z-sime-modal flex items-center justify-center p-4',
          'transition-opacity duration-150',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={pending ? undefined : onClose}
      >
        <div className="absolute inset-0 bg-sime-overlay" />
        <div
          onClick={(e) => { e.stopPropagation(); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-adjunto-title"
          className={cn(
            'relative w-full max-w-[380px] rounded-2xl p-6 bg-sime-surface',
            'shadow-neu-xl',
            'transition-all duration-150',
            visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
          )}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-xl bg-sime-danger/10 shrink-0">
              <Trash2 className="h-5 w-5 text-sime-danger" />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                id="delete-adjunto-title"
                className="text-[17px] font-semibold text-sime-text-primary"
              >
                Eliminar archivo
              </h3>
              <p className="text-sm text-sime-text-secondary mt-1">
                Vas a eliminar{' '}
                <span className="font-medium text-sime-text-primary break-all">
                  {fileName}
                </span>
                . Esta accion no se puede deshacer.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              className="px-4 py-2 rounded-xl text-sm font-medium text-sime-text-secondary hover:text-sime-text-primary transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={pending}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-sime-danger hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {pending ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
}
